/**
 * 兑换邮件链接：设新密码（`reset`），或者直接登录（`login`）。
 *
 * 两种用途共用这一块 —— 它们的风险模型相同（都会**替换这台设备上的身份**），
 * 所以复用与 `EmailLoginSheet` 同一套「当前账号还押着东西就先确认一次」的闸。
 * 区别只在 `reset` 多一组密码输入、成功屏多一句后果说明。
 */
import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import type { PublicAccount } from '@poker/shared'

import { IconAlert } from '../lobby/lobbyIcons.js'
import { groupRoomCode } from '../lobby/roomCode.js'
import type { EmailLinkPurpose } from '../app/emailLinkToken.js'
import { passwordProblem } from './emailAuthApi.js'
import { EmailLinkError, redeemEmailLink } from './emailLinkApi.js'
import { IconKey } from '../icons/index.js'
import { SheetShell } from './sheetShell.js'
import type { AtRiskTable } from './atRisk.js'
import './account.css'

export type RedeemStage = 'input' | 'confirm' | 'working' | 'done'

function totalChips(tables: readonly AtRiskTable[]): number {
  return tables.reduce((n, t) => n + t.stack, 0)
}

/**
 * 按下确定之后该发生什么。抽成纯函数，好让「有筹码时必须先确认」能被 SSR 静态渲染钉死。
 *
 * ★ `login` 用途没有密码字段，所以它只过「确认闸」这一关。
 */
export type RedeemStep =
  | { readonly kind: 'reject'; readonly message: string }
  | { readonly kind: 'confirm' }
  | { readonly kind: 'go' }

export function nextRedeemStep(
  purpose: EmailLinkPurpose,
  password: string,
  again: string,
  atRisk: readonly AtRiskTable[] = [],
): RedeemStep {
  if (purpose === 'reset') {
    const problem = passwordProblem(password)
    if (problem !== null) return { kind: 'reject', message: problem }
    if (password !== again) return { kind: 'reject', message: '两次输入的密码不一样' }
  }
  if (atRisk.length > 0) return { kind: 'confirm' }
  return { kind: 'go' }
}

export interface EmailLinkRedeemSheetProps {
  readonly purpose: EmailLinkPurpose
  readonly token: string
  onClose?(): void
  onDone?(account: PublicAccount): void
  /** 当前账号还坐着的牌桌。非空 = 换身份之前要确认一次 */
  readonly atRisk?: readonly AtRiskTable[]
  /** 注入点：测试用 */
  readonly redeem?: (token: string, password: string | null) => Promise<PublicAccount>
  readonly initialStage?: RedeemStage
  readonly autoFocus?: boolean
}

export function EmailLinkRedeemSheet({
  purpose,
  token,
  onClose,
  onDone,
  atRisk = [],
  redeem,
  initialStage,
  autoFocus = false,
}: EmailLinkRedeemSheetProps): ReactNode {
  const reset = purpose === 'reset'
  // ★ login 用途没有任何要填的东西：进来就该在兑换，不该先愣在一个空白面板上
  const [stage, setStage] = useState<RedeemStage>(initialStage ?? (reset ? 'input' : 'working'))
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)

  const run = useCallback((): void => {
    setStage('working')
    setError(null)
    const call = redeem ?? ((t: string, p: string | null) => redeemEmailLink(t, p))
    void call(token, reset ? password : null).then(
      (account) => {
        setStage('done')
        setPassword('') // 密码不在内存里多留一秒
        setAgain('')
        onDone?.(account)
      },
      (e: unknown) => {
        setError(e instanceof EmailLinkError || e instanceof Error ? e.message : '没能完成，再试一次')
        // ★ login 没有输入屏可回，停在 working 会变成一个转不完的圈：回到一个能看见错误的静止态
        setStage(reset ? 'input' : 'confirm')
      },
    )
  }, [redeem, token, reset, password, onDone])

  const submit = useCallback((): void => {
    const step = nextRedeemStep(purpose, password, again, atRisk)
    if (step.kind === 'reject') {
      setError(step.message)
      return
    }
    if (step.kind === 'confirm') {
      setStage('confirm')
      return
    }
    run()
  }, [purpose, password, again, atRisk, run])

  // login 用途：没有确认闸时直接开跑（挂载即兑换）
  const autoRun = !reset && stage === 'working' && atRisk.length === 0
  if (autoRun && initialStage === undefined) {
    // 首次渲染同步触发一次。放 effect 里会多闪一帧空白面板。
    queueMicrotask(() => {
      if (stage === 'working') run()
    })
  }

  return (
    <SheetShell
      title={reset ? '设置新密码' : '邮件登录'}
      icon={<IconKey size={20} />}
      dismissible={stage !== 'working'}
      {...(onClose === undefined ? {} : { onClose })}
    >
      {error !== null && (
        <div className="lobby-error" role="alert">
          <IconAlert size={18} />
          <span>{error}</span>
        </div>
      )}

      {stage === 'confirm' && (
        <>
          {/* 不可逆：这台设备上当前那个账号会被换掉，它身上的筹码带不走 */}
          <p className="acct-say">
            继续之后这台设备就换成另一个账号了。现在这个账号
            {atRisk.length === 1 ? (
              <>
                在 <span className="code">{groupRoomCode(atRisk[0]?.roomCode ?? '')}</span>{' '}
              </>
            ) : (
              <>
                在 <span className="num">{atRisk.length}</span> 张桌上{' '}
              </>
            )}
            还有 <span className="num">{totalChips(atRisk).toLocaleString()}</span> 筹码，换过去就回不来了。
          </p>
          <div className="acct-actions">
            <button type="button" className="lobby-cta pressable" onClick={run}>
              继续
            </button>
            <button type="button" className="lobby-btn lobby-btn--ghost" onClick={() => onClose?.()}>
              取消
            </button>
          </div>
        </>
      )}

      {stage === 'done' && (
        <>
          <p className="acct-status acct-status--ok" role="status">
            {reset ? '密码改好了，已经登录' : '已登录，你的牌桌都在'}
          </p>
          {/* 撤销其它设备的会话是不可逆后果，说一次；登录什么都不动，不说 */}
          {reset && <p className="acct-say">其它设备已经登出，需要的话重新登录一次。</p>}
          <button type="button" className="lobby-cta pressable" onClick={() => onClose?.()}>
            回大厅
          </button>
        </>
      )}

      {reset && (stage === 'input' || stage === 'working') && (
        <>
          <div className="acct-field">
            <label className="acct-field__label" htmlFor="email-link-new-password">
              新密码
            </label>
            <input
              id="email-link-new-password"
              className="acct-input acct-input--text"
              type="password"
              name="new-password"
              value={password}
              disabled={stage === 'working'}
              autoFocus={autoFocus}
              autoComplete="new-password"
              placeholder="至少 8 位"
              onChange={(e) => {
                setPassword(e.target.value)
                if (error !== null) setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit()
              }}
            />
          </div>
          <div className="acct-field">
            <label className="acct-field__label" htmlFor="email-link-again">
              再输一次
            </label>
            <input
              id="email-link-again"
              className="acct-input acct-input--text"
              type="password"
              name="new-password-again"
              value={again}
              disabled={stage === 'working'}
              autoComplete="new-password"
              placeholder="再输一次"
              onChange={(e) => {
                setAgain(e.target.value)
                if (error !== null) setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit()
              }}
            />
          </div>
          <p className="acct-status" aria-live="polite">
            {stage === 'working' ? '正在设置…' : ''}
          </p>
          <button type="button" className="lobby-cta pressable" disabled={stage === 'working'} onClick={submit}>
            确定
          </button>
        </>
      )}

      {!reset && stage === 'working' && (
        <p className="acct-status" aria-live="polite">
          正在登录…
        </p>
      )}
    </SheetShell>
  )
}
